import { TZDateMini } from '@date-fns/tz';
import clsx from 'clsx';
import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
  useState,
  FocusEventHandler,
  type FocusEvent,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { useIntl } from 'react-intl';

import { _useAriaLabelingProps as useAriaLabelingProps } from '@dynatrace/strato-components/core';
import { Flex } from '@dynatrace/strato-components/layouts';
import { getTimezone } from '@dynatrace-sdk/user-preferences';

import { DateSelector } from './DateSelector.js';
import { dateSelectorCSS, dateTimePickerCSS } from './DateTimePicker.sty.js';
import { getDateTimePickerError } from './utils/get-error-message.js';
import { useFormControlState } from '../../core/hooks/useFormControlState.js';
import { useFocusRing } from '../../core/styles/useFocusRing.js';
import { inputGroupClassName } from '../../layouts/input-group/InputGroup.sty.js';
import { InputGroup } from '../../layouts/input-group/InputGroup.js';
import { fieldColorsCSS } from '../../styles/field.sty.js';
import { DateTimeInput } from '../date-time-range-picker/DateTimeInput.js';
import { dateTimeRangeCSS } from '../date-time-range-picker/DateTimeRangePicker.sty.js';
import {
  isEqualTimeValue,
  removePrecisionFromTimeValue,
} from '../date-time-range-picker/helpers.js';
import type {
  DateTimeError,
  DateTimeInputRef,
} from '../date-time-range-picker/types.js';
import { InternalBaseItem } from '../forms-core/components/form-field-messages/FormFieldMessageBaseItem.js';
import { useDateTimeMessages } from '../forms-core/hooks/useDateTimeMessages.js';
import { useFormFieldMessagesContext } from '../forms-core/hooks/useFormFieldMessagesContext.js';
import { getErrorType } from '../forms-core/hooks/useFormFieldValidation.js';
import { useMinMaxLogger } from '../forms-core/hooks/useMinMaxLogger.js';
import { useSyncedFormControlProps } from '../forms-core/hooks/useSyncedFormControlProps.js';
import { FormFieldMessagesAction } from '../forms-core/state/formfield-messages-state-reducer.js';
import { getTimeValue } from '../forms-core/time/timeParser.js';
import { useInputHint } from '../hooks/useInputHint.js';
import type { FormControlWithOverlayRef } from '../shared-types.js';
import type { DateSelectorRef, DateTimePickerProps } from './types.js';
import { focusDateTimeInput } from './utils/focusDateTimeInput.js';

const SDK_LOGGER_NAME = 'Strato: DateTimePicker';
/**
 * For 'date' type, it adjusts the time by the timezone offset to ensure the
 * date is correct without time information.
 * For 'time' and 'datetime' types, it returns the ISO string as is.
 */
const getInternalValue = (
  isoString: string | undefined,
  type: 'time' | 'date' | 'datetime',
) => {
  if (type === 'date') {
    const newDate = new TZDateMini(isoString ?? '', getTimezone());
    if (!isNaN(newDate.getTime())) {
      newDate.setMinutes(newDate.getMinutes() + newDate.getTimezoneOffset());
      return newDate.toISOString();
    }
  }
  return isoString ?? '';
};

/**
 * Component to comfortably enter a date time value.
 * The `DateTimePicker` component automatically uses the user's settings for the
 * locale and timezone and formats the date and time accordingly.
 * @public
 */
export const DateTimePicker = forwardRef<
  FormControlWithOverlayRef<HTMLSpanElement>,
  DateTimePickerProps
>((props, forwardedRef) => {
  const {
    id: propId,
    disabled: propDisabled,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    readOnly = false,
    required: propRequired,
    'aria-disabled': ariaDisabledProp,
    precision = 'minutes',
    placeholder,
    value: propValue,
    defaultValue: propDefaultValue,
    onChange,
    min,
    max,
    name,
    type = 'datetime',
    controlState,
    ...remainingProps
  }: DateTimePickerProps = props;

  // log to the sdk when min is larger than max.
  useMinMaxLogger(min, max, SDK_LOGGER_NAME);

  const touchedContainerRef = useRef<HTMLDivElement>(null);

  const {
    controlId,
    state: formFieldState,
    dispatch: formFieldDispatch,
  } = useFormFieldMessagesContext();
  const { showError: showFormFieldError } = formFieldState;

  // Backwards compatibility for controlState.
  const [hasFormControlError, hint] = useFormControlState(controlState);
  // _useInputHint will be replaced with new validation concept. APPDEV-8298
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  const { hintProps } = useInputHint({
    hint,
    hasError: hasFormControlError,
  });

  const showError = hasFormControlError || showFormFieldError;

  const { disabled, required, id, ariaDisabled } = useSyncedFormControlProps({
    propDisabled,
    propRequired,
    propId: propId ?? controlId,
    propAriaDisabled: ariaDisabledProp,
  });
  const disabledOrAriaDisabled = disabled || ariaDisabled || false;

  const convertedPropValue = getTimeValue(propValue, precision, type);

  const [internalValue, setInternalValue] = useState(
    propValue
      ? convertedPropValue
      : getTimeValue(propDefaultValue, precision, type),
  );
  const [prevPropValue, setPrevPropValue] = useState(
    getTimeValue(propValue, precision, type) ?? null,
  );

  if (
    propValue !== '' &&
    !isEqualTimeValue(convertedPropValue ?? null, prevPropValue)
  ) {
    setPrevPropValue(convertedPropValue ?? null);
    setInternalValue(convertedPropValue);
  }

  const ariaLabelingProps = useAriaLabelingProps(remainingProps);

  const dateInputRef = useRef<DateTimeInputRef>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const dateSelectorRef = useRef<DateSelectorRef>(null);

  // No need to show the calendar widget if only a time value can be entered.
  const showDateSelector = type !== 'time';

  const { focusClassName } = useFocusRing({
    focusWithin: true,
    color: showError ? 'critical' : 'neutral',
    disabled,
  });

  const intl = useIntl();
  const calendarTriggerLabel = intl.formatMessage({
    defaultMessage: 'Datepicker calendar trigger',
    id: 'Uuc6xAAPyctyHamM',
    description: 'Text shown for the datetimepicker calendar trigger.',
  });

  // Provide root and input element reference and functions to interact with the calendar overlay
  useImperativeHandle(
    forwardedRef,
    (): FormControlWithOverlayRef<HTMLSpanElement> => ({
      element: rootRef.current,
      inputRef: dateInputRef.current?.inputRef,
      triggerRef: dateInputRef.current?.inputRef,
      open: () => dateSelectorRef.current?.open(),
      close: () => dateSelectorRef.current?.close(),
      focus() {
        dateInputRef.current?.inputRef?.focus();
      },
      validate() {
        dateInputRef.current?.validate();
        formFieldDispatch?.({
          type: FormFieldMessagesAction.SETISTOUCHED,
          payload: { isTouched: true },
        });
      },
    }),
    [formFieldDispatch],
  );

  // mark the component as touched, if the touchedRef container is losing the focus
  const onBlurTouched: FocusEventHandler = useCallback(
    (e) => {
      if (!touchedContainerRef.current?.contains(e.relatedTarget)) {
        //  always validate and show error on blur if there is any
        dateInputRef.current?.validate();
        formFieldDispatch?.({
          type: FormFieldMessagesAction.SETISTOUCHED,
          payload: { isTouched: true },
        });
      }
    },
    [formFieldDispatch],
  );

  const dateTimePickerErrorId = useId();
  const { formatString } = useDateTimeMessages({ precision });

  const handleDateTimeInputError = useCallback(
    (error: DateTimeError) => {
      // Remove old errors.
      formFieldDispatch?.({
        type: FormFieldMessagesAction.REMOVEMESSAGE,
        payload: {
          id: dateTimePickerErrorId,
          messageType: 'formcontrol',
        },
      });

      if (error !== null && error !== 'valueMissing') {
        // To avoid duplicate error messages for e.g. 'valueMissing', we try to get the
        // validity key from the native input first and fall back to 'customError'.
        const validityKey =
          dateInputRef.current &&
          getErrorType(dateInputRef.current.inputRef?.validity);

        if (
          !validityKey ||
          (validityKey === 'valueMissing' && error === 'invalid')
        ) {
          formFieldDispatch?.({
            type: FormFieldMessagesAction.ADDMESSAGE,
            payload: {
              id: dateTimePickerErrorId,
              validityStateKey: 'customError',
              variant: 'error',
              message: getDateTimePickerError(
                intl,
                formatString,
                error,
                min,
                max,
                precision,
              ),
              messageType: 'formcontrol',
            },
          });
        }
      }
    },
    [
      dateTimePickerErrorId,
      formFieldDispatch,
      formatString,
      intl,
      max,
      min,
      precision,
    ],
  );

  // remove the textContent when the value is set to null.
  useEffect(() => {
    if (
      propValue === null &&
      dateInputRef.current?.expressionInputRef &&
      dateInputRef.current.expressionInputRef.textContent !== ''
    ) {
      dateInputRef.current.expressionInputRef.textContent = '';
    }
  }, [propValue]);

  return (
    <Flex
      gap={4}
      flexDirection="column"
      data-testid={dataTestId}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      ref={rootRef}
      className={consumerClassName}
      style={consumerStyle}
    >
      <InputGroup
        className={clsx(
          dateTimePickerCSS({
            type,
            disabled: disabledOrAriaDisabled,
            showError,
            readOnly,
          }),
        )}
        style={{ gridAutoColumns: '1fr auto' }}
      >
        <div
          ref={touchedContainerRef}
          className={clsx(
            fieldColorsCSS({
              color: showError ? 'critical' : 'neutral',
              disabled: disabledOrAriaDisabled,
              variant: 'emphasized',
            }),
            dateTimeRangeCSS({
              showError,
              disabled: disabledOrAriaDisabled,
              readOnly,
            }),
            focusClassName,
            inputGroupClassName,
          )}
          tabIndex={disabled ? undefined : 0}
          onBlur={onBlurTouched}
          onKeyDown={(e: ReactKeyboardEvent) => {
            // Forward the focus when a key is pressed in this element.
            if (e.target === touchedContainerRef.current) {
              focusDateTimeInput(null, dateInputRef);
            }
          }}
          onFocus={(e: FocusEvent) => {
            // Forward the focus only if it is coming from outside this component.
            // Focus the first spin button with textContent; otherwise, focus the input.
            if (!rootRef.current?.contains(e.relatedTarget)) {
              focusDateTimeInput(e, dateInputRef);
            }
          }}
        >
          <DateTimeInput
            ref={dateInputRef}
            id={id}
            type={type}
            precision={precision}
            disabled={disabled}
            readOnly={readOnly}
            placeholder={placeholder}
            name={name}
            value={internalValue}
            onChange={(value) => {
              if (typeof value === 'string') {
                onChange?.(value);
                return;
              }

              // Call onChange for null only when the textContent is empty.
              if (
                value !== null ||
                dateInputRef.current?.expressionInputRef?.textContent === ''
              ) {
                onChange?.(
                  removePrecisionFromTimeValue(
                    value,
                    type === 'date' ? 'day' : precision,
                  ),
                );
              }

              setInternalValue(getTimeValue(value, precision, type));
            }}
            {...ariaLabelingProps}
            aria-disabled={ariaDisabled}
            min={min}
            max={max}
            required={required}
            onError={handleDateTimeInputError}
            controlId={id}
            controlState={controlState}
          />
        </div>
        {showDateSelector && (
          <DateSelector
            ref={dateSelectorRef}
            value={getInternalValue(internalValue?.absoluteDate, type)}
            onChange={(value) => {
              setInternalValue(getTimeValue(value, precision, type));

              requestAnimationFrame(() => {
                focusDateTimeInput(null, dateInputRef);
              });
            }}
            aria-label={calendarTriggerLabel}
            aria-disabled={ariaDisabled}
            min={min}
            max={max}
            precision={precision}
            readOnly={readOnly}
            disabled={!!disabled}
            error={showError}
            className={clsx(dateSelectorCSS, inputGroupClassName)}
            data-dtrum-mask={dataDtrumMask}
            data-dtrum-allow={dataDtrumAllow}
          />
        )}
      </InputGroup>
      {hint && (
        <InternalBaseItem
          variant={hasFormControlError ? 'error' : 'hint'}
          disabled={disabled || ariaDisabled}
          {...hintProps}
        >
          {hint}
        </InternalBaseItem>
      )}
    </Flex>
  );
});

(
  DateTimePicker as typeof DateTimePicker & { displayName: string }
).displayName = 'DateTimePicker';
